/**
 * 공지 종류.
 * CLOSING 은 사이트 운영 종료 공지로, 게시 후 1개월이 지나야 종료를 신청할 수 있다.
 */
export type NoticeType = "GENERAL" | "CLOSING";

// 공지사항 타입
export type Notice = {
  id: number;
  companyId: number;
  title: string;
  content: string;
  author: string;
  noticeType: NoticeType;
  isPublished: boolean;
  views: number;
  createdAt: string;
  updatedAt: string;
};

// FAQ 카테고리 Enum
export enum FaqCategory {
  ORDER_PAYMENT = "ORDER_PAYMENT",
  DELIVERY = "DELIVERY",
  CANCEL_REFUND = "CANCEL_REFUND",
  PRODUCT = "PRODUCT",
  ETC = "ETC"
}

// FAQ 카테고리 표시 이름
export const FAQ_CATEGORY_LABELS: Record<FaqCategory, string> = {
  [FaqCategory.ORDER_PAYMENT]: "주문/결제",
  [FaqCategory.DELIVERY]: "배송",
  [FaqCategory.CANCEL_REFUND]: "취소/환불",
  [FaqCategory.PRODUCT]: "상품",
  [FaqCategory.ETC]: "기타"
};

// FAQ 타입
export type Faq = {
  id: number;
  companyId: number;
  category: FaqCategory;
  question: string;
  answer: string;
  author: string;
  isPublished: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
};

// 공지사항 생성/수정 요청
export type NoticeCreateRequest = {
  title: string;
  content: string;
  isPublished: boolean;
  noticeType: NoticeType;
};

export type NoticeUpdateRequest = NoticeCreateRequest;

// FAQ 생성/수정 요청
export type FaqCreateRequest = {
  category: FaqCategory;
  question: string;
  answer: string;
  isPublished: boolean;
};

export type FaqUpdateRequest = FaqCreateRequest;

export type FaqOrderUpdateRequest = {
  displayOrder: number;
};

// 팝업 타입
export type Popup = {
  id: number;
  companyId: number;
  name: string;
  imageUrl: string | null;
  isEnabled: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string | null;
};

// 팝업 순서 변경 요청
export type PopupReorderRequest = {
  popupIds: number[];
};

// 폰트 타입
export type Font = {
  id: number;
  companyId: number;
  fontName: string;
  fontFamily: string;
  fontType: string;
  importText: string;
  isBeingUsed: boolean;
  createdAt: string;
  updatedAt: string | null;
};

export type FontCreateRequest = {
  fontName: string;
  fontFamily: string;
  fontType: string;
  importText: string;
  isBeingUsed: boolean;
};

export type FontUpdateRequest = FontCreateRequest;

// 포트폴리오 추가 이미지
export type PortfolioImage = {
  id: number;
  portfolioId: number;
  imageUrl: string;
  displayOrder: number;
};

/** 본문 아래에 붙는 버튼의 크기 */
export type PortfolioButtonSize = "SMALL" | "MEDIUM" | "LARGE";

/**
 * 포트폴리오 본문 아래에 노출되는 버튼.
 *
 * 마크다운 본문 안에 버튼 문법을 넣지 않고 별도 목록으로 관리한다.
 * 커스텀 마크다운 문법은 파서 확장이 필요하고 XSS 표면을 넓히는 반면,
 * 목록으로 두면 label 과 url 을 각각 검증할 수 있다.
 */
export type PortfolioButton = {
  id: number;
  label: string;
  url: string;
  size: PortfolioButtonSize;
  displayOrder: number;
};

/**
 * 포트폴리오에 연결된 주문.
 *
 * **상품명과 옵션 문구만 담는다.** 수량·단가·금액·주문자/수령인 정보는
 * 이 타입에 필드 자체가 없어야 한다 — 나중에 누가 `null` 처리만 하고
 * 넘어가는 일이 없도록 한다.
 */
export type PortfolioLinkedOrder = {
  orderId: number;
  productName: string;
  /** 사람이 읽을 옵션 문구만. 금액은 들어가지 않는다 */
  options: string[];
};

// 포트폴리오 타입
export type Portfolio = {
  id: number;
  companyId: number;
  title: string;
  summary: string | null;
  /** 마크다운 본문. 마크다운 도입 이전에 저장된 값은 plain text 다 */
  description: string | null;
  clientName: string | null;
  thumbnailUrl: string | null;
  /** 진행 시기 (yyyy-MM-dd) */
  workedAt: string | null;
  isPublished: boolean;
  displayOrder: number;
  images: PortfolioImage[];
  /**
   * 아래 둘은 백엔드 배포가 MES 보다 늦을 수 있어 optional 이다.
   * 상세 응답에만 채워지고 목록 응답에는 없을 수도 있다.
   */
  buttons?: PortfolioButton[] | null;
  linkedOrders?: PortfolioLinkedOrder[] | null;
  createdAt: string;
  updatedAt: string | null;
};

// 포트폴리오 생성/수정 요청 (이미지는 multipart 로 따로 전달)
export type PortfolioCreateRequest = {
  title: string;
  summary?: string;
  description?: string;
  clientName?: string;
  /** 진행 시기 (yyyy-MM-dd). 빈 문자열이면 서버에서 null 로 처리된다 */
  workedAt?: string;
  isPublished: boolean;
};

export type PortfolioUpdateRequest = PortfolioCreateRequest;

// 포트폴리오 순서 변경 요청
export type PortfolioReorderRequest = {
  portfolioIds: number[];
};

/**
 * 버튼 목록 전체 치환 요청 (PUT .../portfolios/{id}/buttons).
 *
 * 기존 포트폴리오 수정 API 는 multipart 라 JSON 배열을 싣기 번거롭고
 * 이미지 교체와 관심사가 달라서 엔드포인트를 분리했다.
 * 배열 순서가 곧 display_order 다 (서버가 그렇게 읽는다).
 */
export type PortfolioButtonsUpdateRequest = {
  buttons: {
    label: string;
    url: string;
    size: PortfolioButtonSize;
  }[];
};

/** 연결 주문 전체 치환 요청 (PUT .../portfolios/{id}/orders). 배열 순서가 displayOrder */
export type PortfolioOrderLinksUpdateRequest = {
  orderIds: number[];
};

// 포트폴리오 섹션 노출 여부 (쇼핑몰에 섹션 자체를 켜고 끄는 스위치)
export type PortfolioSection = {
  isEnabled: boolean;
};

// API 응답 타입
export type GetNoticesResponse = Notice[];
export type GetFaqsResponse = Faq[];
